/**
 * Centralized route configuration for PWA Handel
 * 
 * Single source of truth for:
 * - Reserved system paths
 * - Slug validation patterns
 * - Public route detection
 * 
 * Used by: main.tsx, App.tsx, AuthPage.tsx, ProtectedRoute.tsx, navigateToAuth.ts
 */

export const RESERVED_PATHS = new Set([
  '/',
  '/home',
  '/dashboard',
  '/shop',
  '/cart',
  '/account',
  '/auth',
  '/bankid-callback',
  '/onboarding',
  '/email-onboarding',
  '/contact',
  '/settings',
  '/settings/modules',
  '/payment',
  '/payment-success',
  '/payment/swish-return',
  '/topup',
  '/wallet',
  '/orders',
  '/devices',
  '/loyalty',
  '/debug',
  '/my-tickets',
  '/notifications',
  '/blocked',
  '/security',
  '/terms',
  '/privacy',
  '/pickup-success',
  '/return-success',
  '/pwa-preview',
]);

export const SLUG_PATTERN = /^[a-z0-9-]+$/;

/**
 * Normalize a path: remove trailing slashes
 */
export function normalizePath(path: string): string {
  return (path.replace(/\/+$/, '') || '/');
}

/**
 * Normalize a slug string: decode URI, trim, lowercase
 */
export function normalizeSlug(slug: string): string {
  try {
    return decodeURIComponent(slug).trim().toLowerCase();
  } catch {
    return (slug || '').trim().toLowerCase();
  }
}

/**
 * Check if a path matches the slug pattern (/:slug)
 * and is not a reserved system route
 */
export function isSlugPath(path: string): boolean {
  const normalized = normalizePath(path);
  
  // Extract first segment (e.g., /my-tickets/123 -> /my-tickets)
  const match = normalized.match(/^\/([a-z0-9-]+)(\/.*)?$/);
  
  if (!match) return false;
  
  const firstSegment = `/${match[1]}`;
  
  // If first segment is reserved, it's not a slug path
  if (RESERVED_PATHS.has(firstSegment)) return false;
  
  // Also check the full path for exact matches
  if (RESERVED_PATHS.has(normalized)) return false;
  
  return true;
}

/**
 * Validate a slug string (without leading slash)
 */
export function isValidSlug(slug: string): boolean {
  return SLUG_PATTERN.test(slug);
}

/**
 * Check if a path should be treated as public (no auth required)
 * 
 * Public paths:
 * - Organization landing pages (/:slug)
 * - Authentication flows (/auth, /bankid-callback, /onboarding, /email-onboarding)
 * - Payment returns (/payment/*)
 * - Join routes (/join/:slug)
 */
export function isPublicPath(path: string): boolean {
  const normalized = normalizePath(path);
  
  // Check if it's a slug-based landing page
  if (isSlugPath(normalized)) {
    return true;
  }
  
  // Explicit public routes
  const publicRoutes = [
    '/auth',
    '/bankid-callback',
    '/onboarding',
    '/email-onboarding',
    '/shop',
    '/cart',
    '/blocked',
  ];
  
  if (publicRoutes.includes(normalized)) {
    return true;
  }
  
  // Payment routes, shop routes, and join routes
  if (normalized.startsWith('/payment') || normalized.startsWith('/join/') || normalized.startsWith('/shop/')) {
    return true;
  }
  
  return false;
}
